import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';

import { Close } from '@mui/icons-material';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';

import { dialogHeaderStyles } from '../../../../Common/components/Styles/InputStyles';

import {
  paginationSx,
  bodyRowSx,
  headCellSx
} from '../../../../Common/components/Listing/styles';

const rows = [
  {
    id: 1,
    date: '05/03/2026',
    type: 'Physician Order',
    physician: 'Henry Low MD',
    description: 'Arrhythmia evaluation and medication adjustment.'
  },
  {
    id: 2,
    date: '08/03/2026',
    type: 'Lab Order',
    physician: 'John Smith MD',
    description: 'CBC, CMP, fasting lipid panel ordered.'
  },
  {
    id: 3,
    date: '10/03/2026',
    type: 'Prescription',
    physician: 'Henry Low MD',
    description: 'Metformin 500 mg twice daily.'
  },
  {
    id: 4,
    date: '12/03/2026',
    type: 'Imaging Order',
    physician: 'Alice William MD',
    description: 'Chest X-ray to evaluate persistent cough.'
  },
  {
    id: 5,
    date: '15/03/2026',
    type: 'Physician Order',
    physician: 'John Smith MD',
    description: 'Follow-up appointment in two weeks.'
  },
  {
    id: 6,
    date: '18/03/2026',
    type: 'Lab Order',
    physician: 'Henry Low MD',
    description: 'TSH and Free T4 requested.'
  },
  {
    id: 7,
    date: '20/03/2026',
    type: 'Prescription',
    physician: 'Alice William MD',
    description: 'Amoxicillin 500 mg for seven days.'
  },
  {
    id: 8,
    date: '23/03/2026',
    type: 'Imaging Order',
    physician: 'John Smith MD',
    description: 'MRI lumbar spine without contrast.'
  },
  {
    id: 9,
    date: '26/03/2026',
    type: 'Physician Order',
    physician: 'Henry Low MD',
    description: 'Continue current treatment plan.'
  },
  {
    id: 10,
    date: '29/03/2026',
    type: 'Lab Order',
    physician: 'Alice William MD',
    description: 'Urinalysis and urine culture.'
  },
  {
    id: 11,
    date: '02/04/2026',
    type: 'Prescription',
    physician: 'John Smith MD',
    description: 'Vitamin D3 1000 IU daily.'
  },
  {
    id: 12,
    date: '05/04/2026',
    type: 'Physician Order',
    physician: 'Henry Low MD',
    description: 'Monitor blood pressure at home.'
  },
  {
    id: 13,
    date: '08/04/2026',
    type: 'Lab Order',
    physician: 'Alice William MD',
    description: 'Hemoglobin A1C and fasting glucose.'
  },
  {
    id: 14,
    date: '12/04/2026',
    type: 'Imaging Order',
    physician: 'John Smith MD',
    description: 'Abdominal ultrasound evaluation.'
  },
  {
    id: 15,
    date: '15/04/2026',
    type: 'Prescription',
    physician: 'Henry Low MD',
    description: 'Lisinopril 10 mg once daily.'
  }
];

const columns = [
  {
    field: 'date',
    headerName: 'DATE',
    width: '15%'
  },
  {
    field: 'type',
    headerName: 'TYPE',
    width: '20%'
  },
  {
    field: 'physician',
    headerName: 'PHYSICIAN',
    width: '25%'
  },
  {
    field: 'description',
    headerName: 'DESCRIPTION',
    width: '40%'
  }
];

const cellSx = {
  px: 1.5,
  py: 0,
  height: 38,
  color: '#424242',
  fontSize: 12,
  fontWeight: 400,
  borderBottom: '1px solid #EEEEEE',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
};

const Modal_OrderHx = ({ open, onCancel }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const visibleRows = useMemo(() => {
    const start = page * rowsPerPage;

    return rows.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage]);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleClose = () => {
    onCancel();
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle sx={dialogHeaderStyles.dialogheader}>
        <Box>
          <Typography sx={dialogHeaderStyles.title}>Order Hx</Typography>

          {/* <Typography sx={dialogHeaderStyles.subtitle}>
            Review previous physician, laboratory and imaging orders.
          </Typography> */}
        </Box>

        <IconButton
          aria-label="Close order history dialog"
          onClick={handleClose}
          size="small"
          sx={dialogHeaderStyles.dialogheaderclose}>
          <Close sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: '16px !important',
          bgcolor: '#FFFFFF',
          overflow: 'hidden'
        }}>
        <Paper
          variant="outlined"
          sx={{
            height: 420,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderColor: '#E0E0E0',
            borderRadius: '8px',
            boxShadow: 'none'
          }}>
          <TableContainer
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'auto',

              '&::-webkit-scrollbar': {
                display: 'none'
              },

              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 750,
                tableLayout: 'fixed'
              }}>
              <TableHead>
                <TableRow>
                  {columns.map(column => (
                    <TableCell
                      key={column.field}
                      align={column.align || 'left'}
                      sx={{
                        ...headCellSx,
                        width: column.width
                      }}>
                      {column.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleRows.map(row => (
                  <TableRow key={row.id} hover sx={bodyRowSx}>
                    {columns.map(column => (
                      <TableCell
                        key={column.field}
                        align={column.align || 'left'}
                        sx={{
                          ...cellSx,
                          width: column.width
                        }}>
                        {column.field === 'description' ? (
                          <Typography
                            component={RouterLink}
                            to="#"
                            title={row[column.field]}
                            sx={{
                              display: 'block',
                              color: '#4E8AC7',
                              fontSize: 12,
                              textDecoration: 'none',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',

                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}>
                            {row[column.field]}
                          </Typography>
                        ) : (
                          <Typography
                            title={row[column.field]}
                            sx={{
                              color: '#424242',
                              fontSize: 12,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis'
                            }}>
                            {row[column.field]}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {visibleRows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{
                        py: 4,
                        borderBottom: 0
                      }}>
                      <Typography
                        sx={{
                          color: '#757575',
                          fontSize: 13
                        }}>
                        No order history found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 15, 25]}
            labelRowsPerPage="Rows per page:"
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            sx={paginationSx}
          />
        </Paper>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 2.25,
          bgcolor: '#FFFFFF',
          borderTop: '1px solid #D9D9D9'
        }}>
        <Button
          variant="contained"
          onClick={onCancel}
          size="small"
          sx={{
            bgcolor: '#5897D4',
            color: '#FFFFFF',
            borderRadius: '4px',
            boxShadow: 'none',
            fontSize: 12,
            fontWeight: 500,
            textTransform: 'uppercase',

            '&:hover': {
              bgcolor: '#4B89C5',
              boxShadow: 'none'
            }
          }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

Modal_OrderHx.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default Modal_OrderHx;